import { canClearAnswer } from "@/lib/forms/deselect";
import {
  type NoteAnswerChanges,
  hiddenNoteQuestionIds,
  mergeAnswersWithLocalChanges,
} from "@/lib/note-utils";
import { isPatientReportedMedicationQuestion } from "@/lib/patient-reported-medications";
import { noteAnswerValue } from "@/lib/question-conditions";

import {
  type MultiSelect,
  QuestionType,
  type SingleSelect,
  type Text,
} from "@/types/forms/question";
import type {
  NoteDetail,
  NoteQuestionDetail,
  PrefilledAnswer,
} from "@/types/notes";

export interface InitialNoteAnswer {
  questionId: string;
  data: Record<string, unknown>;
  noteAttachmentId: undefined;
  isUserReported?: boolean;
}

function isTextQuestion(question: NoteQuestionDetail): boolean {
  return (
    question.questionType === QuestionType.TEXT ||
    question.questionType === QuestionType.TEXT_AREA
  );
}

/**
 * Returns default answer data based on question type.
 */
export function getDefaultAnswerData(
  question: NoteQuestionDetail,
): Record<string, unknown> {
  switch (question.questionType) {
    case QuestionType.MULTI_SELECT:
    case QuestionType.RADIAL_MULTI_SELECT:
    case QuestionType.TAG:
      return { values: [] };
    case QuestionType.TOGGLE:
      // A clearable yes/no starts unanswered; a plain switch starts on No
      return { value: canClearAnswer(question) ? null : false };
    case QuestionType.TEXT:
    case QuestionType.TEXT_AREA:
      return { value: null };
    default:
      return { value: null };
  }
}

/**
 * The answer a template configures for a question, or null when it sets none.
 * Plain TEXT/TEXT_AREA questions start from their configured prefilledText as
 * editable content, and select questions start with their configured
 * defaultOptions selected. Patient-reported medication questions are excluded
 * so prefilled content is not parsed into the medication tag input.
 */
function getTemplateDefaultAnswerData(
  question: NoteQuestionDetail,
): Record<string, unknown> | null {
  if (
    isTextQuestion(question) &&
    !isPatientReportedMedicationQuestion(question)
  ) {
    const prefilledText = (question.data as Text).prefilledText;
    if (prefilledText) {
      return { value: prefilledText };
    }
  }

  const defaultOptions = (question.data as SingleSelect | MultiSelect)
    .defaultOptions;
  if (defaultOptions?.length) {
    switch (question.questionType) {
      case QuestionType.SINGLE_SELECT:
      case QuestionType.RADIAL_SINGLE_SELECT:
        return { value: defaultOptions[0] };
      case QuestionType.MULTI_SELECT:
      case QuestionType.RADIAL_MULTI_SELECT:
        return { values: [...defaultOptions] };
    }
  }

  return null;
}

/** Initial answer data for a question that has no patient-record prefill. */
export function getInitialAnswerData(
  question: NoteQuestionDetail,
): Record<string, unknown> {
  return (
    getTemplateDefaultAnswerData(question) ?? getDefaultAnswerData(question)
  );
}

/**
 * Seed a new note with an empty answer per question.
 *
 * Conditional questions are skipped unless the patient record actually prefilled
 * one: a rule may never match, and seeding a default would leave the note
 * carrying an answer for a question that was never shown. Anything the chart
 * did supply is real content and is kept regardless.
 *
 * A question inside a gated section counts as conditional for the same reason.
 */
export function buildInitialNoteAnswers(
  questions: NoteQuestionDetail[],
  prefilledAnswers: PrefilledAnswer[] = [],
  gatedSectionIds: ReadonlySet<string> = new Set(),
): InitialNoteAnswer[] {
  const prefilledMap = new Map(
    prefilledAnswers.map((answer) => [answer.questionId, answer]),
  );

  return questions
    .filter((question) => !question.isArchived)
    .flatMap((question) => {
      const prefilled = prefilledMap.get(question.id);
      const isGated =
        !!question.conditions?.length ||
        (!!question.sectionId && gatedSectionIds.has(question.sectionId));
      if (!prefilled && isGated) return [];
      return [
        {
          questionId: question.id,
          data: prefilled?.data ?? getInitialAnswerData(question),
          noteAttachmentId: undefined,
          ...(prefilled?.isUserReported
            ? { isUserReported: prefilled.isUserReported }
            : {}),
        },
      ];
    });
}

/** Ids of sections carrying any visibility rule, for `buildInitialNoteAnswers`. */
export function gatedSectionIds(
  sections: { id: string; conditions?: unknown[] | null }[] | undefined,
): Set<string> {
  return new Set(
    (sections ?? []).filter((s) => s.conditions?.length).map((s) => s.id),
  );
}

/**
 * Template defaults for questions an edit reveals, which
 * `buildInitialNoteAnswers` left out of the new note. A question that already
 * holds a value keeps it.
 */
export function revealedQuestionDefaults(
  note: NoteDetail,
  before: NoteAnswerChanges,
  after: NoteAnswerChanges,
): { answerId: string; data: Record<string, unknown> }[] {
  const wasHidden = hiddenNoteQuestionIds(note, before);
  if (wasHidden.size === 0) return [];
  const isHidden = hiddenNoteQuestionIds(note, after);
  const answers = new Map(
    mergeAnswersWithLocalChanges(note, after).map((a) => [a.questionId, a]),
  );

  return (note.template?.questions ?? []).flatMap((question) => {
    if (
      question.isArchived ||
      !wasHidden.has(question.id) ||
      isHidden.has(question.id)
    ) {
      return [];
    }
    const answer = answers.get(question.id);
    const value = noteAnswerValue(answer, question.questionType);
    const isEmpty =
      value == null ||
      value === "" ||
      (Array.isArray(value) && value.length === 0);
    if (!isEmpty) return [];
    const data = getTemplateDefaultAnswerData(question);
    return data
      ? [{ answerId: answer?.id ?? `temp-${question.id}`, data }]
      : [];
  });
}
